import * as t from 'io-ts';
import _, {isUndefined} from 'lodash';
import {compose} from 'redux';
import uuid from 'uuid-random';

import {createCountersUpdaterForConversations} from '../../../src/helpers/counterHelpers';
import {
  applyTrackerBumpedOfflineId,
  BumpedAtFinder,
  isConversationInTeamContext,
  updateConversationTrackerOffline,
} from '../../../src/helpers/models/conversationHelpers';
import {makeEnumRuntimeType} from '../../../src/models/api/apiTypes';
import {ApiConversationBatch} from '../../../src/models/api/conversationBatchModel';
import {serializeConversationPatch} from '../../../src/models/api/conversationPatchModel';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../../src/models/api/conversationTrackerModel';
import {mergeDataGroups} from '../../../src/models/api/dataGroupModel';
import {patchApiAsync} from '../../../src/services/http/http';
import {HttpNetworkError, HttpTimeoutError} from '../../../src/services/http/httpError';
import {StateGetter} from '../../../src/store/appStore';
import {getCompany} from '../../../src/store/data/company/companySelectors';
import {updateClientCountersForConversationsAction} from '../../../src/store/data/counters/countersActions';
import {updateDataAction} from '../../../src/store/data/dataGroupsActions';
import {getConversations} from '../../../src/store/data/dataSelectors';
import {DataState} from '../../../src/store/data/dataState';
import {CellsContext} from '../../../src/store/panels/cells/context/cellsContext';
import {OfflineError} from '../../../srcCommon/services/offline/offlineError';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../helpers/updateHelpers';
import {createTask, createTaskType} from '../task';

/*
 * Task.
 */

/** This offline task is for all offline actions that bump the teammate's tracker. */
// TODO: Rename this action to be more accurate.
export const RUpdateConversationsTrackerStatusTask = createTaskType({
  name: 'updateConversationsTrackerStatus',
  payloadType: t.intersection([
    t.type({
      conversations: t.readonlyArray(
        t.intersection([
          t.type({
            id: t.number,
            lastBumpedAt: t.number,
            offlineBumpedId: t.string,
          }),
          t.partial({
            bumpedAt: t.number,
          }),
        ]),
      ),
      teammateId: t.number,
      bumpedAt: t.number,
      initiatedAt: t.union([t.number, t.undefined]),
    }),
    t.partial({
      stage: t.union([makeEnumRuntimeType(ConversationTrackerStagesEnum), t.undefined]),
      status: t.union([makeEnumRuntimeType(ConversationTrackerStatusesEnum), t.undefined]),
      // A number to set a reminder, null to clear a reminder, undefined to leave reminders unchanged.
      remindAt: t.union([t.number, t.null, t.undefined]),
    }),
  ]),
  createOptimisticUpdater: ({
    conversations,
    teammateId,
    stage,
    status,
    bumpedAt: defaultBumpedAt,
    remindAt,
  }) =>
    createCountersUpdaterForConversations(
      conversations.map(({id}) => id),
      createObjectPropertyUpdater<DataState, 'conversations'>(
        'conversations',
        createObjectPropertyUpdater(
          'byId',
          compose(
            ...conversations.map(({id, bumpedAt, lastBumpedAt, offlineBumpedId}) =>
              createResourceMapValueUpdater(
                id,
                createUpdater(
                  compose(
                    updateConversationTrackerOffline({
                      teammateId,
                      lastBumpedAt,
                      offlineBumpedId,
                      stage,
                      status,
                      bumpedAt: !isUndefined(bumpedAt) ? bumpedAt : defaultBumpedAt,
                      remindAt,
                    }),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  createPerformer:
    ({conversations, teammateId, stage, status, bumpedAt: defaultBumpedAt, remindAt, initiatedAt}) =>
    async (dispatch, getState, finishOptimisticUpdate) => {
      try {
        // TODO: Should we be using state.originalData.company just in case?
        const company = getCompany(getState());
        const url = `${company.url}/conversations`;
        const body = {
          conversations: conversations.map(({id, bumpedAt, lastBumpedAt, offlineBumpedId}) =>
            serializeConversationPatch(id, {
              updateTrackers: [
                {
                  teammateId,
                  stage,
                  status,
                  bumpedAt: !isUndefined(bumpedAt) ? bumpedAt : defaultBumpedAt,
                  remindAt,
                },
              ],
              offlineMetadata: {
                trackers: [
                  {
                    teammateId,
                    lastBumpedAt,
                    offlineBumpedId,
                  },
                ],
                initiatedAt,
              },
            }),
          ),
        };

        const {dataGroups} = await patchApiAsync(ApiConversationBatch, url, body);
        if (dataGroups.length !== conversations.length) {
          throw new Error(
            `Expected ${conversations.length} conversation batch results, received ${dataGroups.length}`,
          );
        }

        const dataGroupsWithOfflineIds = _(conversations)
          .zip(dataGroups)
          .map(
            ([conversation, dataGroup]) =>
              conversation &&
              dataGroup &&
              applyTrackerBumpedOfflineId(teammateId, conversation.offlineBumpedId, dataGroup),
          )
          .compact()
          .value();
        const mergedDataGroup = mergeDataGroups(...dataGroupsWithOfflineIds);

        // Update our state + finish the optimistic update + update client-side counters.
        // TODO: Make this prettier and reduce duplication.
        dispatch(
          updateClientCountersForConversationsAction(
            conversations.map(({id}) => id),
            () => {
              finishOptimisticUpdate(updateDataAction(mergedDataGroup));
            },
          ),
        );
      } catch (error) {
        if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
          throw new OfflineError(error);
        }

        throw error;
      }
    },
});

/*
 * Task creator.
 */

interface UpdateConversationsTrackerStatusTaskOptions {
  shouldAllowAssigneeContext?: boolean;
  shouldAllowTeamContext?: boolean;
  stage?: ConversationTrackerStagesEnum;
  status?: ConversationTrackerStatusesEnum;
  findBumpedAt?: BumpedAtFinder;
  remindAt?: number | null;
}

export function createUpdateConversationsTrackerStatusTask(
  getState: StateGetter,
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
  options: UpdateConversationsTrackerStatusTaskOptions = {},
) {
  const {teammateId} = cellsContext;
  const {shouldAllowAssigneeContext, shouldAllowTeamContext, status, stage, findBumpedAt, remindAt} = options;

  const state = getState();
  const allConversations = getConversations(state);

  const conversations = _(conversationIds)
    .map((id) => {
      const conversation = allConversations[id];
      if (!conversation) {
        return undefined;
      }

      // Skip assignee or team contexts.
      const isAssignee = teammateId === conversation.assigneeId;
      const isTeamContext = isConversationInTeamContext(cellsContext, conversation);
      if ((!shouldAllowAssigneeContext && isAssignee) || (!shouldAllowTeamContext && isTeamContext)) {
        return undefined;
      }

      // Skip if the user is not already tracker.
      const tracker = conversation.trackers[teammateId];
      if (!tracker) {
        return undefined;
      }

      // Use the optimistic state from any previously scheduled tasks.
      return {
        id,
        bumpedAt: findBumpedAt && findBumpedAt(conversation.id),
        lastBumpedAt: tracker.bumpedAt,
        offlineBumpedId: tracker.offlineBumpedId || uuid(),
      };
    })
    .compact()
    .value();
  if (conversations.length === 0) {
    return undefined;
  }

  return createTask(RUpdateConversationsTrackerStatusTask, {
    conversations,
    teammateId,
    stage,
    status,
    bumpedAt: Date.now(),
    remindAt,
    initiatedAt: Date.now(),
  });
}
